import { X } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import type { TaskTagDto } from "@/api/workTasks";

interface Props {
  tags: TaskTagDto[];
  className?: string;
  /** Caller-provided remove callback. Editable when supplied. */
  onRemove?: (tagId: string) => void;
}

/**
 * Tag-color tokens carried as design-system token names ("gold.DEFAULT",
 * "status.info", ...). Map them to the tailwind classes the codebase
 * already exposes via globals.css; unknown tokens fall back to a neutral
 * outline so an admin-renamed tag never blows up the UI.
 */
const TOKEN_TO_CLASS: Record<string, string> = {
  "gold.DEFAULT": "bg-gold text-onyx",
  "gold.shadow": "bg-gold-shadow text-white-pure",
  "status.info": "bg-status-info text-white-pure",
  "status.success": "bg-status-success text-white-pure",
  "status.error": "bg-status-error text-white-pure",
  "status.warning": "bg-status-warning text-white-pure",
  "gray.mid": "bg-gray-mid text-white-pure",
};

function tagClass(token: string | null | undefined): string {
  if (token && TOKEN_TO_CLASS[token]) return TOKEN_TO_CLASS[token];
  return "bg-white-warm text-onyx border border-border-light";
}

export function TaskTagChips({ tags, className, onRemove }: Props) {
  if (tags.length === 0) {
    return (
      <span className="text-label text-gray-mid">No tags</span>
    );
  }
  return (
    <div className={cn("flex flex-wrap gap-space-2", className)}>
      {tags.map((t) => (
        <span
          key={t.id}
          className={cn(
            "inline-flex items-center gap-space-1 px-space-2 py-[3px] rounded-sm",
            "font-display text-[10px] font-bold uppercase tracking-[0.12em]",
            tagClass(t.color),
          )}
        >
          {t.displayName}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(t.id)}
              aria-label={`Remove ${t.displayName} tag`}
              className="ml-space-1 -mr-space-1 hover:opacity-80"
            >
              <X size={10} aria-hidden />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}
